import React from "react";
import HelpMedia from "./HelpMedia";

const HelpGlobalNetwork = () => {
  return (
    <div className="pt-3">
      <h3>Global Network Page</h3>
      <p>
        The Global Network page provides an interactive version of the HAP1 genetic interaction profile
        similarity network. Nodes in this network represent library genes, and edges connect pairs of library
        genes that share similar patterns of genetic interactions and whose genetic interaction profile
        similarity exceed a defined Pearson Correlation Coefficient (PCC) threshold (PCC &ge; 0.41).
      </p>

      <h5 className="mt-4">Key Features:</h5>
      <ul>
        <li>Interactive network visualization</li>
        <li>Search for genes of interest</li>
        <li>Redirect to external databases</li>
        <li>Network zoom and pan controls</li>
        <li>Capture and save network views as PNG images</li>
      </ul>

      <h5 className="mt-4 ">How To Use:</h5>
      <div className="mb-4">
        <h6 className="fw-semibold">1. Global Network View - Landing Page</h6>
        <HelpMedia
          type="image"
          src={`${process.env.PUBLIC_URL}/images/help/network_landing_page.png`}
          alt="Global Network View - Landing Page"
        />
      </div>
      <div className="mb-4">
        <h6 className="fw-semibold">2. Gene Search</h6>
        <p>
          The specific location of a gene can be mapped onto the global profile similarity network by
          inputting the gene name into the search bar. Type a gene name to see suggestions, then
          click 'Enter' to add it to the selection. Multiple gene search is allowed. A gene can be removed
          from the selection by clicking the X button beside the gene name.
        </p>
        <p>
          Following a gene search, the position of the selected gene on the network is indicated by a red node.
          Additionally, the general biological processes associated with each cluster, as determined by
          <a href="https://doi.org/10.1016/j.cels.2016.04.014" target="_blank" rel="noreferrer"
             className="text-decoration-none"> Spatial Analysis of Functional Enrichment (SAFE)</a>, are also
          highlighted with different colors.
        </p>
        <p>
          Genes that do not meet the minimum threshold (PCC &ge; 0.41), will not appear on the global
          network. Instead, a warning message will appear in the bottom-right corner of the page, indicating that data
          for these genes can still be accessed through the Subnetwork View or List View pages.
        </p>
        <HelpMedia
          src={`${process.env.PUBLIC_URL}/videos/help/network_gene_search.webm`}
          poster={`${process.env.PUBLIC_URL}/images/help/network_gene_search.png`}
          alt="Gene Search Result"
        />
      </div>
      <div className="mb-4">
        <h6 className="fw-semibold">3. Node Actions</h6>
        <ul>
          <li>
            Double-click on any node to open the
            <a href="https://www.genecards.org" target="_blank" rel="noreferrer"
               className="text-decoration-none"> GeneCards</a> database gene page for the selected gene.
          </li>
          <li>Right-click on any node to open a list of links that redirect to one of the following databases:
            <a href="https://www.genecards.org" target="_blank" rel="noreferrer"
               className="text-decoration-none"> GeneCards</a>,
            <a href="https://depmap.org" target="_blank" rel="noreferrer"
               className="text-decoration-none"> DepMap</a>,
            <a href="https://www.alliancegenome.org" target="_blank" rel="noreferrer"
               className="text-decoration-none"> Alliance Genome </a> and
            <a href="https://www.uniprot.org" target="_blank" rel="noreferrer"
               className="text-decoration-none"> UniProt</a>.
          </li>
        </ul>
        <HelpMedia
          src={`${process.env.PUBLIC_URL}/videos/help/network_node_actions.webm`}
          poster={`${process.env.PUBLIC_URL}/images/help/network_node_actions.png`}
          alt="Node Actions"
        />
      </div>
      <div className="mb-4">
        <h6 className="fw-semibold">4. Zoom and Pan Controls</h6>
        <ul>
          <li>
            A vertical stack of four icons at the bottom-left corner of the page lets the user view/hide gene name
            labels Zoom in, Reset, and Zoom out the network view. Scrolling the mouse wheel up or down also adjusts the
            zoom level.
          </li>
          <li>
            To move the view horizontally or vertically without changing the zoom level, the user can click
            and hold the mouse wheel, then drag to pan across the content.
          </li>
        </ul>
        <HelpMedia
          src={`${process.env.PUBLIC_URL}/videos/help/network_zoom_and_pan.webm`}
          poster={`${process.env.PUBLIC_URL}/images/help/network_gene_search.png`}
          alt="Zoom and Pan Controls"
        />
      </div>
      <div className="mb-4">
        <h6 className="fw-semibold">5. Screen Capture</h6>
        <p>
          The camera icon, located in the bottom-left corner of the page (above the zoom icons), allows the
          user to save the network shown on the screen as a Portable Network Graphics (.png) image.
        </p>
        <HelpMedia
          src={`${process.env.PUBLIC_URL}/videos/help/network_screen_capture.webm`}
          poster={`${process.env.PUBLIC_URL}/images/help/network_screen_capture.png`}
          alt="Screen Capture"
        />
      </div>
    </div>
  );
}

export default HelpGlobalNetwork;